// Agents are square, people are round (Paper "Family"). Built on shadcn Avatar.
import { Avatar, AvatarFallback } from "@crew/ui/components/avatar";
import { cn } from "@crew/ui/lib/utils";
import type { ReactNode } from "react";

export type AgentTint = "ink" | "sage" | "peach";
export type PersonTint = "sand" | "sage" | "rose";

const agentTint: Record<AgentTint, string> = {
  ink: "bg-ink text-background font-display",
  sage: "bg-agent-sage text-foreground font-semibold",
  peach: "bg-agent-peach text-foreground font-semibold",
};

/** Chief of Staff: ink with a serif glyph. Specialists: tinted with a short sans glyph. */
export function AgentAvatar({
  glyph,
  tint = "ink",
  size = 22,
  working = false,
  awake = false,
  className,
}: {
  glyph: string;
  tint?: AgentTint;
  size?: number;
  /** Moss ring while the agent is working. */
  working?: boolean;
  /** A moss presence dot in the bottom corner while the computer is awake. */
  awake?: boolean;
  className?: string;
}) {
  const radius = Math.round(size * 0.27);
  const fontSize = tint === "ink" ? Math.round(size * 0.64) : Math.max(9, Math.round(size * 0.45));
  return (
    <Avatar
      data-slot="agent-avatar"
      aria-hidden
      className={cn(
        "after:hidden",
        working && "shadow-[0_0_0_2px_var(--color-card),0_0_0_3.5px_var(--color-moss)]",
        className,
      )}
      style={{ width: size, height: size, borderRadius: radius }}
    >
      <AvatarFallback
        className={cn("leading-none", agentTint[tint])}
        style={{ borderRadius: radius, fontSize }}
      >
        {glyph}
      </AvatarFallback>
      {awake ? (
        <span
          data-slot="agent-presence"
          className="absolute -right-[3px] -bottom-[3px] size-[9px] rounded-full border-2 border-sidebar bg-moss"
        />
      ) : null}
    </Avatar>
  );
}

const personTint: Record<PersonTint, string> = {
  sand: "bg-person-sand",
  sage: "bg-person-sage",
  rose: "bg-person-rose",
};

export function PersonAvatar({
  initials,
  tint = "sand",
  size = 28,
  className,
}: {
  initials: string;
  tint?: PersonTint;
  size?: number;
  className?: string;
}) {
  return (
    <Avatar
      data-slot="person-avatar"
      aria-hidden
      className={cn("after:hidden", className)}
      style={{ width: size, height: size }}
    >
      <AvatarFallback
        className={cn("leading-none font-semibold text-foreground", personTint[tint])}
        style={{ fontSize: size <= 24 ? 9 : 11 }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

/** Tight row of small avatars (who's in a thread). */
export function AvatarRow({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("flex shrink-0 gap-0.5", className)}>{children}</span>;
}

/** Small square avatar + agent name ("$ Money manager"). */
export function AgentChip({
  name,
  glyph,
  tint,
  size = 20,
  className,
}: {
  name: string;
  glyph: string;
  tint: AgentTint;
  size?: number;
  className?: string;
}) {
  return (
    <span
      data-slot="agent-chip"
      className={cn("inline-flex items-center gap-2 text-meta text-copy", className)}
    >
      <AgentAvatar glyph={glyph} tint={tint} size={size} />
      <span className="truncate">{name}</span>
    </span>
  );
}

/** Round avatar + name, with an optional muted prefix ("For  MB Marcus"). */
export function PersonChip({
  name,
  initials,
  tint,
  prefix,
  size = 20,
  className,
}: {
  name: string;
  initials: string;
  tint?: PersonTint;
  prefix?: string;
  size?: number;
  className?: string;
}) {
  return (
    <span
      data-slot="person-chip"
      className={cn("inline-flex items-center gap-2 text-meta text-copy", className)}
    >
      {prefix ? <span className="text-muted-foreground">{prefix}</span> : null}
      <PersonAvatar initials={initials} tint={tint} size={size} />
      <span className="truncate">{name}</span>
    </span>
  );
}
